import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ALL_CATEGORIES_ID } from '../category/model';
import {
  getCategories,
  getCategoriesById,
  getSelectedId,
  getTreeData,
} from '../category/selectors';
import { getProjectsById, getSelectedIds as getSelectedProjectIds } from '../project/selectors';
import * as actions from './actions';
import { getUploadToProjectEnabled } from './selectors';
import { useUploader } from './useUploader';

// Pseudo-option of the destination picker: upload into a folder created on the fly.
export const NEW_FOLDER_KEY = '__new_folder__';

export type UploadTarget = ReturnType<typeof useUploadTarget>;

/**
 * Destination of an upload (project → existing folder or a new one) shared by the upload
 * modal and the empty-catalog upload panel. `resetKey` drops the typed folder name, e.g.
 * when the modal is reopened.
 */
export function useUploadTarget(resetKey?: unknown) {
  const dispatch = useDispatch();
  const { upload } = useUploader();
  const enabled = useSelector(getUploadToProjectEnabled);
  const treeData = useSelector(getTreeData);
  const categories = useSelector(getCategories);
  const categoriesById = useSelector(getCategoriesById);
  const selectedCategoryId = useSelector(getSelectedId);
  const selectedProjectIds = useSelector(getSelectedProjectIds);
  const projectsById = useSelector(getProjectsById);

  const projectName =
    selectedProjectIds.length === 1 ? projectsById[selectedProjectIds[0]]?.name ?? null : null;
  const categorySelected = selectedCategoryId !== ALL_CATEGORIES_ID;

  // Children of the "All categories" pseudo-root: uploading into "All" is not allowed.
  const folderTreeData = treeData[0]?.children ?? [];
  const destinationTreeData = [{ key: NEW_FOLDER_KEY, title: '+ New folder' }, ...folderTreeData];

  // The picker starts on the sidebar's folder. With "All categories" a project's only
  // folder (the Demo "Your space" → "Files") is preselected, so a first-time user does
  // not have to invent a folder name; otherwise it starts on "New folder".
  const defaultPick =
    categorySelected && categoriesById[selectedCategoryId]
      ? selectedCategoryId
      : categories.length === 1
        ? categories[0].id
        : NEW_FOLDER_KEY;
  const [picked, setPicked] = useState<string>(defaultPick);
  const [newFolderName, setNewFolderName] = useState('');
  useEffect(() => {
    setPicked(defaultPick);
    setNewFolderName('');
  }, [resetKey, defaultPick]);

  const newFolderActive = picked === NEW_FOLDER_KEY;
  const pickedCategory = newFolderActive ? null : categoriesById[picked] ?? null;
  const trimmedFolderName = newFolderName.trim();
  const folderNameTaken =
    trimmedFolderName !== '' &&
    categories.some(
      (c) => c.path.length === 0 && c.name.toLowerCase() === trimmedFolderName.toLowerCase(),
    );
  const uploadBlocked =
    !enabled ||
    (newFolderActive ? trimmedFolderName === '' || folderNameTaken : pickedCategory === null);
  const blockedText = newFolderActive
    ? 'Enter a folder name to upload files'
    : 'Select a folder to upload files';

  const selectValue = newFolderActive ? NEW_FOLDER_KEY : pickedCategory?.id;

  // `openModal`: show the progress in the upload modal (the empty-catalog panel has none).
  const startUpload = (files: File[], { openModal = false } = {}) => {
    if (uploadBlocked || files.length === 0) return;
    // Creating a folder takes a round trip: show "Preparing upload…" meanwhile.
    if (openModal || newFolderActive) {
      dispatch(actions.modalOpened({ pendingUpload: true }));
    }
    void upload(
      files,
      newFolderActive ? { newFolderName: trimmedFolderName } : { categoryId: pickedCategory?.id },
    );
  };

  return {
    projectName,
    // Folder chosen in the sidebar (null for "All categories") — for the empty-state copy.
    sidebarFolderName: categorySelected ? categoriesById[selectedCategoryId]?.name ?? null : null,
    destinationTreeData,
    selectValue,
    selectDestination: setPicked,
    newFolderActive,
    newFolderName,
    setNewFolderName,
    folderNameTaken,
    uploadBlocked,
    blockedText,
    startUpload,
  };
}
